import { notify } from '@/lib/toast';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DollarSign, Clock, CheckCircle, Coins } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { FilterChips } from '@/components/ui/filter-chips';
import { TabBar } from '@/components/ui/tab-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Dropdown } from 'primereact/dropdown';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TipDisbursement {
  id: string;
  orderId: string;
  orderNumber: string;
  waiterId: string;
  waiterName: string;
  amount: number;
  status: 'PENDING' | 'PENDING_APPROVAL' | 'DISBURSED';
  disbursedAt: string | null;
  createdAt: string;
}

interface TipListResponse {
  data: TipDisbursement[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface WaiterSummary {
  userId: string;
  name: string;
  totalTips: number;
  pendingTips: number;
  disbursedTips: number;
  orderCount: number;
}

interface TipSummaryResponse {
  waiters: WaiterSummary[];
  totals: {
    totalTips: number;
    totalPending: number;
    totalDisbursed: number;
  };
}

/* ------------------------------------------------------------------ */
/*  Date helpers                                                       */
/* ------------------------------------------------------------------ */

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function startOfWeekISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().split('T')[0];
}

function startOfMonthISO(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function TipManagementPage() {
  const { t } = useTranslation('restaurant');
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Disburse confirm
  const [disburseWaiter, setDisburseWaiter] = useState<WaiterSummary | null>(null);

  // In-page view: split the two long tables so only one scrolls at a time
  const [tab, setTab] = useState<'waiters' | 'records'>('waiters');

  /* ---------- Queries ---------- */

  const summaryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    return p.toString();
  }, [dateFrom, dateTo]);

  const { data: summary, isLoading: summaryLoading } = useQuery<TipSummaryResponse>({
    queryKey: ['tips-summary', summaryParams],
    queryFn: () =>
      api.get(`/tips/summary${summaryParams ? `?${summaryParams}` : ''}`).then((r) => r.data),
  });

  const listParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('limit', String(pageSize));
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    if (statusFilter) p.set('status', statusFilter);
    return p.toString();
  }, [page, dateFrom, dateTo, statusFilter]);

  const { data: tipsData, isLoading: tipsLoading } = useQuery<TipListResponse>({
    queryKey: ['tips', listParams],
    queryFn: () => api.get(`/tips?${listParams}`).then((r) => r.data),
  });

  /* ---------- Mutations ---------- */

  const disburseMutation = useMutation({
    mutationFn: (ids: string[]) => api.post('/tips/disburse', { ids }).then((r) => r.data),
    onSuccess: (data: { disbursed?: number; pendingApproval?: number }) => {
      if (data?.pendingApproval) {
        toast.success(t('tipsPendingApproval'));
      } else {
        toast.success(t('tipsDisburseSuccess'));
      }
      queryClient.invalidateQueries({ queryKey: ['tips'] });
      queryClient.invalidateQueries({ queryKey: ['tips-summary'] });
      setDisburseWaiter(null);
    },
    onError: () => {
      toast.error(t('failedToDisburseTips'));
    },
  });

  const approveMutation = useMutation({
    mutationFn: (ids: string[]) => api.post('/tips/approve', { ids }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('tipsApproveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tips'] });
      queryClient.invalidateQueries({ queryKey: ['tips-summary'] });
    },
    onError: (err) => notify.error(err, t('failedToDisburseTips')),
  });

  const rejectMutation = useMutation({
    mutationFn: (ids: string[]) => api.post('/tips/reject', { ids }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('tipsRejectSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tips'] });
      queryClient.invalidateQueries({ queryKey: ['tips-summary'] });
    },
    onError: (err) => notify.error(err, t('failedToDisburseTips')),
  });

  /* ---------- Handlers ---------- */

  const handleDisburseConfirm = () => {
    if (!disburseWaiter) return;
    // Fetch pending tip IDs for this waiter from already loaded tip records,
    // or call with waiterId filter
    const waiterPendingParams = new URLSearchParams();
    waiterPendingParams.set('waiterId', disburseWaiter.userId);
    waiterPendingParams.set('status', 'PENDING');
    waiterPendingParams.set('limit', '1000');
    if (dateFrom) waiterPendingParams.set('dateFrom', dateFrom);
    if (dateTo) waiterPendingParams.set('dateTo', dateTo);

    api
      .get(`/tips?${waiterPendingParams.toString()}`)
      .then((r) => {
        const tips: TipDisbursement[] = r.data?.data ?? r.data ?? [];
        const ids = tips.map((tip) => tip.id);
        if (ids.length === 0) {
          toast.error(t('noTipRecords'));
          setDisburseWaiter(null);
          return;
        }
        disburseMutation.mutate(ids);
      })
      .catch(() => {
        toast.error(t('failedToDisburseTips'));
        setDisburseWaiter(null);
      });
  };

  const applyQuickFilter = (preset: 'today' | 'week' | 'month') => {
    const today = todayISO();
    setPage(1);
    setDateTo(today);
    switch (preset) {
      case 'today':
        setDateFrom(today);
        break;
      case 'week':
        setDateFrom(startOfWeekISO());
        break;
      case 'month':
        setDateFrom(startOfMonthISO());
        break;
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setPage(1);
  };

  const datePreset =
    dateTo === todayISO() && dateFrom === todayISO()
      ? 'today'
      : dateTo === todayISO() && dateFrom === startOfWeekISO()
        ? 'week'
        : dateTo === todayISO() && dateFrom === startOfMonthISO()
          ? 'month'
          : null;

  const tips = tipsData?.data ?? [];
  const total = tipsData?.total ?? 0;
  const totals = summary?.totals ?? { totalTips: 0, totalPending: 0, totalDisbursed: 0 };
  const waiters = summary?.waiters ?? [];

  /* ---------- Render ---------- */

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader title={t('tipManagement')} subtitle={t('tipManagementSubtitle')} />

      {/* -------- Summary Cards -------- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('totalTips')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summaryLoading ? '—' : formatCurrency(totals.totalTips)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pendingDisbursement')}</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning-text">
              {summaryLoading ? '—' : formatCurrency(totals.totalPending)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('disbursed')}</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success-text">
              {summaryLoading ? '—' : formatCurrency(totals.totalDisbursed)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* -------- Date Filters -------- */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="tips-date-from" className="text-muted-foreground whitespace-nowrap">{t('dateFrom')}</Label>
            <input
              id="tips-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-label={t('dateFrom')}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="tips-date-to" className="text-muted-foreground whitespace-nowrap">{t('dateTo')}</Label>
            <input
              id="tips-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-label={t('dateTo')}
            />
          </div>

          <FilterChips
            chips={[
              { key: 'today', label: t('today') },
              { key: 'week', label: t('thisWeek') },
              { key: 'month', label: t('thisMonth') },
            ]}
            value={datePreset}
            onChange={(key) => applyQuickFilter(key as 'today' | 'week' | 'month')}
          />

          {(dateFrom || dateTo || statusFilter) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              ✕
            </Button>
          )}
        </CardContent>
      </Card>

      <TabBar
        tabs={[
          { key: 'waiters', label: t('waiterBreakdown') },
          { key: 'records', label: t('tipRecords') },
        ]}
        activeTab={tab}
        onTabChange={(k) => setTab(k as 'waiters' | 'records')}
        bordered={false}
      />

      {/* -------- Waiter Breakdown -------- */}
      {tab === 'waiters' && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('waiterBreakdown')}</CardTitle>
        </CardHeader>
        <CardContent>
          {summaryLoading ? (
            <SkeletonTable rows={4} cols={6} />
          ) : waiters.length === 0 ? (
            <EmptyState
              icon={Coins}
              title={t('noTipRecords')}
              description={t('tipManagementSubtitle')}
            />
          ) : (
            <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('waiter')}</Th>
                    <Th className="text-end">{t('ordersServed')}</Th>
                    <Th className="text-end">{t('totalTips')}</Th>
                    <Th className="text-end">{t('pendingAmount')}</Th>
                    <Th className="text-end">{t('disbursedAmount')}</Th>
                    <Th className="text-end">{/* Actions */}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {waiters.map((w) => (
                    <Tr key={w.userId}>
                      <Td className="td-name font-medium">{w.name}</Td>
                      <Td label={t('ordersServed')} className="text-end">{w.orderCount}</Td>
                      <Td label={t('totalTips')} className="text-end">{formatCurrency(w.totalTips)}</Td>
                      <Td label={t('pendingAmount')} className="text-end text-warning-text">
                        {formatCurrency(w.pendingTips)}
                      </Td>
                      <Td label={t('disbursedAmount')} className="text-end text-success-text">
                        {formatCurrency(w.disbursedTips)}
                      </Td>
                      <Td className="td-actions text-end">
                        {canWrite && w.pendingTips > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDisburseWaiter(w)}
                          >
                            {t('disburse')}
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
          )}
        </CardContent>
      </Card>
      )}

      {/* -------- Tip Records -------- */}
      {tab === 'records' && (
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">{t('tipRecords')}</CardTitle>
          <Dropdown
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.value); setPage(1); }}
            options={[
              { label: t('allStatuses'), value: '' },
              { label: t('pending'), value: 'PENDING' },
              { label: t('pendingApproval'), value: 'PENDING_APPROVAL' },
              { label: t('disbursed'), value: 'DISBURSED' },
            ]}
            optionLabel="label"
            optionValue="value"
            placeholder={t('allStatuses')}
            className="w-full sm:w-40"
          />
        </CardHeader>
        <CardContent>
          {tipsLoading ? (
            <SkeletonTable rows={6} cols={5} />
          ) : tips.length === 0 ? (
            <EmptyState
              icon={Coins}
              title={t('noTipRecords')}
            />
          ) : (
            <>
              <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('reservationDate')}</Th>
                      <Th>{t('waiter')}</Th>
                      <Th>{t('orderNumber')}</Th>
                      <Th className="text-end">{t('tipAmount')}</Th>
                      <Th>{t('status')}</Th>
                      {canWrite && <Th className="text-end">{t('actions')}</Th>}
                    </Tr>
                  </THead>
                  <TBody>
                    {tips.map((tip) => (
                      <Tr key={tip.id}>
                        <Td label={t('reservationDate')}>{formatDate(tip.createdAt)}</Td>
                        <Td className="td-name">{tip.waiterName}</Td>
                        <Td label={t('orderNumber')}>{tip.orderNumber}</Td>
                        <Td label={t('tipAmount')} className="text-end font-medium">
                          {formatCurrency(tip.amount)}
                        </Td>
                        <Td label={t('status')}>
                          <StatusBadge status={tip.status} />
                        </Td>
                        {canWrite && (
                          <Td className="td-actions text-end">
                            {tip.status === 'PENDING_APPROVAL' ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={approveMutation.isPending}
                                  onClick={() => approveMutation.mutate([tip.id])}
                                >
                                  {t('approve')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={rejectMutation.isPending}
                                  onClick={() => rejectMutation.mutate([tip.id])}
                                >
                                  {t('reject')}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </TBody>
                </Table>

              {total > pageSize && (
                <div className="mt-4">
                  <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* -------- Disburse Confirm Dialog -------- */}
      <ConfirmDialog
        open={!!disburseWaiter}
        title={t('disburseTips')}
        message={t('disburseConfirm', { name: disburseWaiter?.name ?? '' })}
        confirmLabel={t('disburse')}
        variant="warning"
        loading={disburseMutation.isPending}
        onConfirm={handleDisburseConfirm}
        onCancel={() => setDisburseWaiter(null)}
      />
    </div>
  );
}
